import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Landmark, Link2, Plus, Receipt, Undo2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { add, format, parse } from '@fint/money';
import { api, ApiError, qs } from '@/lib/api';
import { usePagedList } from '@/hooks/usePagedList';
import { ListFooter } from '@/features/common/ListFooter';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useIsPhone } from '@/hooks/useIsPhone';
import { formatDate } from '@/lib/dates';
import { PageHeader } from '@/app/DashboardLayout';
import { RecordPaymentDrawer } from './RecordPaymentDrawer';
import { MatchToInvoiceSheet } from './MatchToInvoiceSheet';
import { AdvancedFilter, useAdvancedFilter } from '@/features/common/AdvancedFilter';
import {
  Badge, Button, Card, CardContent, CardFacts, CardLine, CardRow, Cards, EmptyState, ErrorState, Money, SkeletonTable, Table, TBody, TD, TH, THead, TR,
} from '@/features/common/ui';

/** A deposit with an income category and no invoice - see PaymentsService.listDirect. */
interface DirectReceiptRow {
  id: string;
  date: string;
  amount: string;
  description: string | null;
  reference: string | null;
  accountId: string;
  accountName: string;
  currency: string;
  categoryId: string | null;
  categoryName: string | null;
  categoryEmoji: string | null;
  categoryColour: string;
  interBook: boolean;
  /**
   * Who paid and for what, when the deposit has been told - on Banking, or
   * by confirming the AI agent's change in the Inbox. Optional on the wire
   * while the API lands them; without them the row reads as it did.
   */
  partyId?: string | null;
  partyName?: string | null;
  itemId?: string | null;
  itemName?: string | null;
}

interface PaymentRow {
  id: string;
  number: string;
  date: string;
  amount: string;
  currency: string;
  mode: string;
  referenceNumber: string | null;
  /**
   * What the payment says about itself, when the list carries it: on one a
   * settlement recorded (0018) it names the deposit it came from - "Settled
   * from the 12 Aug deposit 'NEFT PKPP TECH'" - which is how the row is found
   * on Banking. Optional on the wire; read only when there is no reference.
   */
  notes?: string | null;
  unappliedAmount: string;
  tdsSection: string | null;
  tdsAmount: string;
  bankCharges: string;
  /** The book's side of a foreign-currency payment (0021). */
  baseCurrency?: string;
  baseAmount?: string;
  customerName: string;
  accountName: string | null;
}

/** The reference as given, else what the payment says about itself - the deposit a settlement came from. */
function referenceLine(row: Pick<PaymentRow, 'referenceNumber' | 'notes'>): string | null {
  return row.referenceNumber?.trim() || row.notes?.trim() || null;
}

/**
 * Payments received.
 *
 * ── WHY THIS SCREEN EXISTS NOW ──
 * Payments had a complete API and no screen at all: they could be recorded by
 * Claude or by an HTTP client and by nothing a person could click. That was
 * survivable while a payment was just an amount. It stopped being survivable
 * the moment TDS became recordable, because TDS is entered ON a payment - and
 * shipping it without this screen would have made it the fourth feature in
 * this project that was built, documented, and unreachable.
 */
export function PaymentsPage() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const isPhone = useIsPhone();
  const [recording, setRecording] = useState(false);
  /** The deposit being matched to the invoice it paid - see MatchToInvoiceSheet. */
  const [matching, setMatching] = useState<DirectReceiptRow | null>(null);
  const queryClient = useQueryClient();

  /**
   * ── UNDO ──
   * A payment matched to the wrong invoice was, until tonight, undone only
   * through the API: the route existed, the button did not, and the owner
   * had ₹30,000 sitting as credit on one invoice while another aged. Undo
   * deletes the payment and its allocations; the invoice's balance comes
   * back, and a deposit the payment settled returns to "received without
   * an invoice" - it was in the bank before the payment and stays there.
   * Confirmed in words first: the same act is how a real payment is lost.
   */
  const undo = useMutation({
    mutationFn: (id: string) => api.delete(`/payments/${id}`),
    onSuccess: () => {
      for (const key of ['payments', 'payments-direct', 'invoices', 'invoice', 'invoice-document', 'invoice-payments', 'banking', 'overview', 'dashboard', 'reports']) {
        void queryClient.invalidateQueries({ queryKey: [key, org?.id] });
      }
    },
  });
  const undoError = undo.error instanceof ApiError ? undo.error : undefined;
  const canUndo = perms.can('payment', 'delete');
  const askUndo = (row: PaymentRow) => {
    const what = `${row.number} - ${row.customerName}, ${row.amount} on ${formatDate(row.date)}`;
    if (window.confirm(`Undo ${what}?\n\nThe invoice gets its balance back. If a bank deposit was matched to this payment it returns to "received without an invoice"; a bank row this payment created is removed.`)) {
      undo.mutate(row.id);
    }
  };

  // The condition builder, in the URL: "UPI payments over ₹10,000 this
  // quarter" is a link. It narrows the invoice-payments list; the direct
  // receipts below are bank rows with their own fields, and stay as they are.
  const advanced = useAdvancedFilter();
  const payments = usePagedList<PaymentRow>({
    key: ['payments', org?.id, advanced.key],
    path: (cursor) => `/payments${qs({ cursor, ...advanced.params })}`,
    enabled: !!org?.id && perms.can('payment', 'view'),
  });
  /**
   * ── THE OTHER HALF OF "RECEIVED" ──
   * Money that arrived with no invoice behind it - a freelancer's fees, a
   * salary, a customer paying on a statement - is a bank deposit with an
   * income category. It is counted as income everywhere else; this page,
   * titled "Payments received", showed "No payments yet" over ₹7.8 lakh of it
   * because it read one table. Both halves are listed now, each saying which
   * it is. Needs `banking.view`, so a role without it sees the invoice half
   * and a sentence.
   */
  const canSeeDirect = perms.can('banking', 'view');
  const direct = usePagedList<DirectReceiptRow>({
    key: ['payments-direct', org?.id],
    path: (cursor) => `/payments/direct${qs({ cursor })}`,
    enabled: !!org?.id && perms.can('payment', 'view') && canSeeDirect,
  });

  if (!perms.can('payment', 'view')) {
    return (
      <>
        <PageHeader title="Payments" />
        <div className="px-5 pb-10">
          <EmptyState
            title="You cannot see payments"
            description="Ask an administrator if you need access."
          />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Payments received"
        subtitle="Every rupee that came in: against an invoice, or straight into the bank."
        actions={
          perms.can('payment', 'create') ? (
            <Button onClick={() => setRecording(true)}>
              <Plus className="mr-1.5 h-4 w-4" />
              Record payment
            </Button>
          ) : null
        }
      />

      <div className="space-y-4 px-5 pb-10">
        <Card>
          <CardContent className="pt-5">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <Receipt className="size-4 text-muted-foreground" aria-hidden />
              Against invoices
            </h2>
            <div className="mb-3">
              <AdvancedFilter entityType="payment" value={advanced.value} onChange={advanced.set} />
            </div>
            {payments.isLoading ? (
              <SkeletonTable rows={5} cols={5} />
            ) : payments.error ? (
              <ErrorState
                error={payments.error}
                onRetry={() => {
                  void payments.refetch();
                }}
              />
            ) : !payments.items.length ? (
              <EmptyState
                icon={Receipt}
                title={advanced.active ? 'Nothing matches that filter' : 'No invoice payments yet'}
                description={
                  advanced.active
                    ? undefined
                    : canSeeDirect
                      ? 'Record one when a customer pays an invoice. Money that arrives without an invoice is listed below.'
                      : 'Record one when a customer pays an invoice. Money that arrives without an invoice is on Banking.'
                }
              />
            ) : (
              <>
              {isPhone ? (
            <Cards>
                {payments.items.map((row) => {
                  const withheld = add(
                    parse(row.tdsAmount ?? '0', row.currency),
                    parse(row.bankCharges ?? '0', row.currency),
                  );
                  return (
                    <CardRow key={row.id} data-testid="payment-row">
                      <CardLine
                        title={row.customerName}
                        // The reference beside the mode, as the table has it: on
                        // a payment a settlement recorded it is the deposit's own
                        // words ("NEFT PKPP TECH"), which is how the row is found
                        // on Banking.
                        meta={[row.number, row.mode.replace(/_/g, ' '), referenceLine(row)].filter(Boolean).join(' · ')}
                        value={<PaidAmount row={row} />}
                        sub={
                          // The withheld figure earns its place on a phone for
                          // the same reason as on a laptop: ₹90,000 against a
                          // ₹1,00,000 invoice reads as a shortfall until the
                          // ₹10,000 that went to the government is beside it.
                          withheld.minor > 0 ? (
                            <>
                              +<Money value={format(withheld)} currency={row.currency} />{' '}
                              {row.tdsSection ? `TDS ${row.tdsSection}` : 'charges'}
                            </>
                          ) : null
                        }
                      />
                      <CardFacts>
                        <span>{formatDate(row.date)}</span>
                        {parse(row.unappliedAmount, row.currency).minor !== 0 ? (
                          <Badge variant="info">
                            <Money value={row.unappliedAmount} currency={row.currency} /> unapplied
                          </Badge>
                        ) : null}
                      </CardFacts>
                      {canUndo ? (
                        <Button type="button" variant="ghost" size="sm" className="mt-1" onClick={() => askUndo(row)} loading={undo.isPending && undo.variables === row.id} data-testid="payment-undo">
                          <Undo2 className="size-3.5" aria-hidden />
                          Undo
                        </Button>
                      ) : null}
                    </CardRow>
                  );
                })}
              </Cards>
            ) : (

                            <Table>
                <THead>
                  <TR>
                    <TH>Payment</TH>
                    <TH>Customer</TH>
                    <TH>Date</TH>
                    <TH numeric>Received</TH>
                    <TH numeric>Withheld</TH>
                    <TH numeric>Unapplied</TH>
                    {canUndo ? <TH className="w-px"><span className="sr-only">Actions</span></TH> : null}
                  </TR>
                </THead>
                <TBody>
                  {payments.items.map((row) => {
                    const withheld = add(
                      parse(row.tdsAmount ?? '0', row.currency),
                      parse(row.bankCharges ?? '0', row.currency),
                    );
                    return (
                      <TR key={row.id} data-testid="payment-row">
                        <TD>
                          <div className="font-medium">{row.number}</div>
                          <div className="max-w-xs truncate text-xs text-muted-foreground" data-testid="payment-reference">
                            {row.mode.replace(/_/g, ' ')}
                            {referenceLine(row) ? ` · ${referenceLine(row)}` : ''}
                          </div>
                        </TD>
                        <TD>{row.customerName}</TD>
                        <TD className="text-muted-foreground">{formatDate(row.date)}</TD>
                        <TD numeric>
                          <PaidAmount row={row} />
                        </TD>
                        <TD numeric>
                          {/*
                            Shown even when zero would be noise, because a
                            ₹90,000 payment against a ₹1,00,000 invoice reads
                            as a shortfall until the ₹10,000 withheld is
                            visible beside it.
                          */}
                          {withheld.minor === 0 ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <div>
                              <Money value={format(withheld)} currency={row.currency} />
                              {row.tdsSection ? (
                                <div className="text-xs text-muted-foreground">
                                  TDS {row.tdsSection}
                                </div>
                              ) : (
                                <div className="text-xs text-muted-foreground">Bank charges</div>
                              )}
                            </div>
                          )}
                        </TD>
                        <TD numeric>
                          {parse(row.unappliedAmount, row.currency).minor === 0 ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <Badge variant="info">
                              <Money value={row.unappliedAmount} currency={row.currency} />
                            </Badge>
                          )}
                        </TD>
                        {canUndo ? (
                          <TD>
                            <Button type="button" variant="ghost" size="sm" onClick={() => askUndo(row)} loading={undo.isPending && undo.variables === row.id} aria-label={`Undo ${row.number}`} data-testid="payment-undo">
                              <Undo2 className="size-3.5" aria-hidden />
                              Undo
                            </Button>
                          </TD>
                        ) : null}
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
              )}
              {undoError ? (
                <p role="alert" className="mt-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {undoError.message}
                </p>
              ) : null}
              <ListFooter query={payments} noun="payments" />
              </>
            )}
          </CardContent>
        </Card>

        {canSeeDirect ? <DirectReceipts direct={direct} isPhone={isPhone} onMatch={perms.can('payment', 'create') ? setMatching : undefined} /> : null}
      </div>

      {recording ? <RecordPaymentDrawer onClose={() => setRecording(false)} /> : null}
      {matching ? <MatchToInvoiceSheet deposit={matching} onClose={() => setMatching(null)} /> : null}
    </>
  );
}

/**
 * Deposits with an income category and no invoice. Read-only here - the row
 * is a bank transaction, and the Banking page is where it is recategorised or
 * excluded - so every row links there.
 */
function DirectReceipts({
  direct,
  isPhone,
  onMatch,
}: {
  direct: ReturnType<typeof usePagedList<DirectReceiptRow>>;
  isPhone: boolean;
  /** "Match to invoice" on a row, for someone who may record payments; absent otherwise. */
  onMatch?: (row: DirectReceiptRow) => void;
}) {
  const label = (row: DirectReceiptRow) => row.description || row.reference || 'Deposit';
  /**
   * "From" is the customer once the deposit knows one - that is the answer
   * to the question the column asks - and the statement's own words until
   * then. The words stay under the name, because they are how the row is
   * found on Banking.
   */
  const from = (row: DirectReceiptRow) => row.partyName?.trim() || label(row);
  const fromDetail = (row: DirectReceiptRow) =>
    row.partyName?.trim() ? label(row) : row.description && row.reference ? row.reference : null;
  return (
    <Card data-testid="direct-receipts">
      <CardContent className="pt-5">
        <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold">
          <Landmark className="size-4 text-muted-foreground" aria-hidden />
          Received without an invoice
        </h2>
        <p className="mb-3 text-xs text-muted-foreground">
          Bank deposits you have given an income category. Counted as income; change or exclude one on{' '}
          <Link to="/banking" className="underline" data-tap-exempt="inline link in a sentence">
            Banking
          </Link>
          .
        </p>
        {direct.isLoading ? (
          <SkeletonTable rows={4} cols={4} />
        ) : direct.error ? (
          <ErrorState
            error={direct.error}
            onRetry={() => {
              void direct.refetch();
            }}
          />
        ) : !direct.items.length ? (
          <EmptyState
            icon={Landmark}
            title="Nothing received without an invoice"
            description="A deposit with an income category shows here the moment you file one - from Banking, Quick add, or your AI agent's proposals once confirmed."
          />
        ) : (
          <>
            {isPhone ? (
              <Cards>
                {direct.items.map((row) => (
                  <CardRow key={row.id} data-testid="direct-receipt-row">
                    <CardLine
                      title={from(row)}
                      meta={[fromDetail(row), formatDate(row.date), row.accountName].filter(Boolean).join(' · ')}
                      value={<Money value={row.amount} currency={row.currency} />}
                    />
                    <CardFacts>
                      <span>
                        {row.categoryEmoji ? `${row.categoryEmoji} ` : ''}
                        {row.categoryName ?? 'Uncategorised'}
                        {row.interBook ? ' · between your books' : ''}
                      </span>
                      {row.itemName ? <span data-testid="direct-receipt-item">For {row.itemName}</span> : null}
                    </CardFacts>
                    {onMatch && !row.interBook ? (
                      <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => onMatch(row)} data-testid="direct-receipt-match">
                        <Link2 className="size-3.5" aria-hidden />
                        Match to invoice
                      </Button>
                    ) : null}
                  </CardRow>
                ))}
              </Cards>
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>From</TH>
                    <TH>Category</TH>
                    <TH>Into</TH>
                    <TH>Date</TH>
                    <TH numeric>Received</TH>
                    {onMatch ? <TH className="w-px"><span className="sr-only">Actions</span></TH> : null}
                  </TR>
                </THead>
                <TBody>
                  {direct.items.map((row) => (
                    <TR key={row.id} data-testid="direct-receipt-row">
                      <TD>
                        <div className="font-medium">{from(row)}</div>
                        {fromDetail(row) ? <div className="text-xs text-muted-foreground">{fromDetail(row)}</div> : null}
                      </TD>
                      <TD>
                        <span className="inline-flex items-center gap-1">
                          {row.categoryEmoji ? <span aria-hidden>{row.categoryEmoji}</span> : null}
                          {row.categoryName ?? 'Uncategorised'}
                          {row.interBook ? (
                            <Badge variant="info">between your books</Badge>
                          ) : null}
                        </span>
                        {/* The item beside the category: "Services · Consulting"
                            is what the invoice line would have said. */}
                        {row.itemName ? (
                          <div className="text-xs text-muted-foreground" data-testid="direct-receipt-item">
                            For {row.itemName}
                          </div>
                        ) : null}
                      </TD>
                      <TD className="text-muted-foreground">{row.accountName}</TD>
                      <TD className="text-muted-foreground">{formatDate(row.date)}</TD>
                      <TD numeric>
                        <Money value={row.amount} currency={row.currency} />
                      </TD>
                      {onMatch ? (
                        <TD>
                          {/* Not for a transfer between the books: that money paid no invoice. */}
                          {!row.interBook ? (
                            <Button type="button" variant="ghost" size="sm" onClick={() => onMatch(row)} data-testid="direct-receipt-match">
                              <Link2 className="size-3.5" aria-hidden />
                              Match to invoice
                            </Button>
                          ) : null}
                        </TD>
                      ) : null}
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
            <ListFooter query={direct} noun="receipts" one="receipt" />
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * What the customer paid and, for a payment in another currency, what the
 * account was credited: "£988.00 · ₹1,25,574.80". The rupee figure is the
 * one the books carry; the pound figure is the one the customer sent.
 */
function PaidAmount({ row }: { row: PaymentRow }) {
  const foreign = !!row.baseCurrency && row.baseCurrency !== row.currency && !!row.baseAmount;
  return (
    <span className="inline-flex flex-col items-end" data-testid={foreign ? 'payment-foreign' : undefined}>
      <Money value={row.amount} currency={row.currency} />
      {foreign ? (
        <span className="text-xs text-muted-foreground">
          <Money value={row.baseAmount ?? '0'} currency={row.baseCurrency ?? 'INR'} />
        </span>
      ) : null}
    </span>
  );
}
